// Admin view for DPDP data-rights requests. List, filter by status, view details,
// and mark requests in_progress / resolved / rejected with optional admin notes.
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCcw, Loader2 } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { v: '', label: 'All statuses' },
  { v: 'open', label: 'Open' },
  { v: 'in_progress', label: 'In progress' },
  { v: 'resolved', label: 'Resolved' },
  { v: 'rejected', label: 'Rejected' },
];

const STATUS_COLORS = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

export default function AdminPrivacyRequestsPage() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API_URL}/api/admin/privacy/requests${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, token]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const updateStatus = async (id, status) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/privacy/requests/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status, admin_notes: noteText || undefined }),
      });
      const data = await res.json();
      if (data.request) setSelected(data.request);
      fetchRequests();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-privacy-requests-page">
      <AppHeader
        title="Data Requests"
        rightSlot={
          <button onClick={fetchRequests} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-white/10 rounded-full" data-testid="admin-privacy-refresh" aria-label="Refresh">
            <RefreshCcw size={18} />
          </button>
        }
      />

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-privacy-status-filter"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-6 flex items-center gap-2 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">No data requests.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">User</th>
                  <th className="text-left px-4 py-2">Created</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it) => (
                  <tr key={it.id} data-testid={`admin-privacy-row-${it.id}`}>
                    <td className="px-4 py-2 capitalize">{(it.request_type || '').replace('_', ' ')}</td>
                    <td className="px-4 py-2 text-gray-600">{it.user_email || it.user_id}</td>
                    <td className="px-4 py-2 text-gray-500">{new Date(it.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[it.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                        {(it.status || '').replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => { setSelected(it); setNoteText(it.admin_notes || ''); }}
                        data-testid={`admin-privacy-view-${it.id}`}
                        className="text-emerald-700 hover:underline text-sm"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="admin-privacy-detail">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold capitalize">{(selected.request_type || '').replace('_', ' ')} request</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600" data-testid="admin-privacy-detail-close">✕</button>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <div><span className="text-gray-400">User:</span> {selected.user_email || selected.user_id}</div>
              {selected.consent_types?.length > 0 && <div><span className="text-gray-400">Consents:</span> {selected.consent_types.join(', ')}</div>}
              {selected.details && <div><span className="text-gray-400">Details:</span> {selected.details}</div>}
              <div><span className="text-gray-400">Privacy version:</span> {selected.privacy_version}</div>
              <div><span className="text-gray-400">Created:</span> {new Date(selected.created_at).toLocaleString()}</div>
            </div>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={2}
              placeholder="Admin notes (optional)"
              data-testid="admin-privacy-notes"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <div className="flex flex-wrap gap-2">
              <button disabled={saving} onClick={() => updateStatus(selected.id, 'in_progress')} data-testid="admin-privacy-set-inprogress" className="text-sm px-3 py-1.5 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50">Mark in progress</button>
              <button disabled={saving} onClick={() => updateStatus(selected.id, 'resolved')} data-testid="admin-privacy-set-resolved" className="text-sm px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50">Mark resolved</button>
              <button disabled={saving} onClick={() => updateStatus(selected.id, 'rejected')} data-testid="admin-privacy-set-rejected" className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
